import {
  Alert,
  Button,
  ButtonSize,
  Input,
  Intent,
  Shape,
  Tooltip,
} from '@axieinfinity/matcha'
import {
  ArrowSquareOutIcon,
  CopySimpleIcon,
  WalletIcon,
  WarningFillIcon,
} from '@axieinfinity/matcha-icons'
import { useState } from 'react'

import type { DefaultWallet, ErrorResponse } from '~/apis'
import { updateProfileV2API } from '~/apis'
import { Box } from '~/components/Box'
import { Ronin } from '~/components/Socials'
import { useAuthContext } from '~/hooks/useAuthContext'
import { useWrappedToast } from '~/hooks/useWrappedToast'
import { shortString } from '~/utils/helper'

import { BadgeDefaultWallet } from './BadgeDefaultWallet'

export const ConnectedRoninWallet: React.FC = () => {
  const { user, userV2, getProfile } = useAuthContext()
  const { showInfoMessage, showSuccessMessage, showError } = useWrappedToast()
  const [isLoading, setIsLoading] = useState(false)

  const roninAddress = userV2?.account?.wallet?.secondary
  const mpcAddress = userV2?.account?.wallet?.identity

  // resolve temporary issue for Ronin Wallet is matching with MPC address
  const isRoninWalletDifferent =
    roninAddress?.toLowerCase() !== mpcAddress?.toLowerCase()

  const defaultWallet =
    userV2?.account?.wallet?.default ||
    (roninAddress && 'wallet.secondary') ||
    (mpcAddress && 'wallet.identity')

  const isDisabled = user?.socialAccounts?.length === 1

  const keylessSection = mpcAddress || userV2?.keylessFailed

  const onClickCopyAddress = (address?: string) => {
    if (!address) return

    navigator.clipboard.writeText(address)
    showInfoMessage('Address copied to clipboard')
  }

  const onClickViewOnExplorer = (address?: string) => {
    if (!address) return

    window.open(
      `https://explorer.roninchain.com/address/${address}`,
      '_blank',
      'noopener noreferrer',
    )
  }

  const handleUpdateDefaultWallet = async (wallet: DefaultWallet) => {
    if (isLoading) return

    setIsLoading(true)
    try {
      await updateProfileV2API(wallet)
      await getProfile()
      showSuccessMessage(
        <div className="text-text-color-subdued text-body-s">
          If the new default wallet has not taken effective in your game, please{' '}
          <span className="text-text-color-default">
            sign out & then sign in to your game account again / Mavis Hub
          </span>
          .
        </div>,
        { title: 'Default wallet set successfully', duration: 20 },
      )
    } catch (err) {
      const error = err as ErrorResponse
      showError(error.error_message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box>
      <div className="p-24 flex flex-col gap-24">
        <div className="text-h6">Manage Wallets</div>
        <div className="matcha-divider-bottom h-[1px]" />

        <div className="flex flex-col md:flex-row md:justify-between gap-40">
          <div className="flex flex-col w-full md:w-1/2">
            <div className="flex gap-8">
              <div className="text-body-m-strong mb-4">Ronin Wallet</div>
              {defaultWallet === 'wallet.secondary' && <BadgeDefaultWallet />}
            </div>
            <div className="text-body-s text-text-color-subdued">
              A seed-phrase wallet that is linked & managed from Ronin Wallet
              App or Ronin Wallet web extension. Assets in this wallet are view
              only in game.
            </div>
          </div>
          {roninAddress && isRoninWalletDifferent ? (
            <div className="flex flex-col gap-16 flex-1 ">
              <Input
                shape={Shape.Default}
                suffixIcon={
                  <div className="flex items-center gap-8">
                    <ArrowSquareOutIcon
                      onClick={() => onClickViewOnExplorer(roninAddress)}
                      className="cursor-pointer"
                    />
                    <CopySimpleIcon
                      onClick={() => onClickCopyAddress(roninAddress)}
                      className="cursor-pointer"
                    />
                  </div>
                }
                readOnly
                value={shortString({
                  str: roninAddress,
                  left: 20,
                  right: 4,
                })}
              />

              <div className="flex flex-col md:flex-row gap-8 justify-end">
                {isDisabled ? (
                  <Tooltip
                    id="ronin"
                    target={
                      <div className="flex justify-end">
                        <Ronin disabled={isDisabled} connected />
                      </div>
                    }
                  >
                    <p className="max-w-[360px] text-center">
                      You can only disconnect Ronin address while having another
                      login method.
                    </p>
                  </Tooltip>
                ) : (
                  <Ronin className="self-end" connected />
                )}

                <Button
                  icon={WalletIcon}
                  className="self-end"
                  size={ButtonSize.Small}
                  shape={Shape.Default}
                  text="Set default wallet"
                  onClick={() => handleUpdateDefaultWallet('wallet.secondary')}
                  disabled={defaultWallet === 'wallet.secondary' || isLoading}
                />
              </div>
            </div>
          ) : (
            <div className="flex justify-end items-center">
              <Ronin />
            </div>
          )}
        </div>
        {keylessSection && (
          <div className="flex flex-col md:flex-row gap-40">
            <div className="flex flex-col w-full md:w-1/2">
              <div className="flex gap-8">
                <div className="text-body-m-strong mb-4">Keyless wallet</div>
                {defaultWallet === 'wallet.identity' && <BadgeDefaultWallet />}
              </div>
              <div className="text-body-s text-text-color-subdued">
                Your Waypoint account comes with a built-in wallet, so you can
                move money & items, get rewards directly in games and
                marketplaces. This wallet can&apos;t be unlinked.
              </div>
            </div>
            {userV2.account.wallet?.identity && userV2.keylessFailed && (
              <Alert
                className="flex-1 !bg-[#251704]"
                intent={Intent.Secondary}
                showCloseIcon={false}
                footer={
                  <Button
                    onClick={() =>
                      window.open(
                        `https://support.roninchain.com/hc/en-us/articles/29088223236763`,
                        '_blank',
                        'noopener noreferrer',
                      )
                    }
                    size={ButtonSize.XSmall}
                    text="Check support article"
                    className="w-fit mt-4"
                    shape={Shape.Rounded}
                    intent={Intent.Primary}
                  />
                }
                icon={WarningFillIcon}
                title="Wallet reset required"
                content={
                  <div className="text-text-color-subdued">
                    Your wallet has not been created or backed up properly due
                    to internet connection issues or unknown problems. Please
                    read this article to create new wallet.
                  </div>
                }
              />
            )}
            {mpcAddress && (
              <div className="flex flex-1 flex-col gap-16">
                <Input
                  shape={Shape.Default}
                  suffixIcon={
                    <div className="flex items-center gap-8">
                      <ArrowSquareOutIcon
                        onClick={() => onClickViewOnExplorer(mpcAddress)}
                        className="cursor-pointer"
                      />
                      <CopySimpleIcon
                        onClick={() => onClickCopyAddress(mpcAddress)}
                        className="cursor-pointer"
                      />
                    </div>
                  }
                  readOnly
                  value={shortString({
                    str: mpcAddress,
                    left: 20,
                    right: 4,
                  })}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </Box>
  )
}
